import { format } from 'date-fns'
import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import { Status } from '../../components/cards/statusTag'
import Icon from '../../components/icon'
import Dropdown from '../../components/inputs/dropdown'
import { UpdateItemStockModal } from '../../components/modal/app_modals/updateItemStockmodal'
import { TableComponent } from '../../components/table'
import {
  calculateProductPriceRaw,
  formatCurrency,
  getVendorName,
} from '../../helpers'
import { useModalControl } from '../../hooks/useModalControl'
import { Product } from '../../redux-toolkits/products/products.type'
import { FulfilmentCentre } from '../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'

interface Props {
  productList: Product[]
  fulfilmentCentres: FulfilmentCentre[]
  currentPage?: number
  totalDataCount?: number
  pageLimit?: number
  onPageChange?: (page: number) => void
  loading?: boolean
  refetch: () => void
}

const ProductsTable: React.FC<Props> = ({
  productList,
  fulfilmentCentres,
  currentPage,
  totalDataCount,
  pageLimit,
  onPageChange,
  loading,
  refetch,
}) => {
  const [selected, setSelected] = useState<Product>()
  const [updateMode, setUpdateMode] = useState<'price' | 'quantity'>('quantity')

  const navigate = useNavigate()

  const { modal, modalOpen, setModalOpen, handleModalOpen, handleModalClose } =
    useModalControl()

  return (
    <>
      <Tooltip anchorSelect={`#noStockEdit`} className="tooltip">
        Stock managed via variants only.
      </Tooltip>

      <div className="w-full overflow-x-scroll">
        <TableComponent
          headers={[
            'Name',
            'Price',
            'Stock quantity',
            'Status',
            'Creation Date',
            'Actions',
          ]}
          rows={productList.map((product) => {
            return {
              id: product._id,
              content: [
                <div
                  key={`${product._id}-name`}
                  className="
                flex min-w-max
                items-center
                gap-2
              "
                >
                  <img
                    key={`${product._id}-image`}
                    height={50}
                    width={50}
                    src={product.images[0]}
                    alt="product"
                    className="
                  w-[40px] 
                  h-[40px]
                  object-cover
                  overflow-hidden rounded-md
                "
                  />

                  <div className="flex flex-col gap-1">
                    <Link
                      to={`/products/view/${product._id}`}
                      className="
                    text-sec-black
                    hover:text-black hover:underline
                    transition-all duration-500
                    text-sm
                    "
                    >
                      {product.name}
                    </Link>

                    <p
                      className="
                    bg-bg-gray
                    px-2 py-1
                    rounded-full
                    max-w-fit
                    text-xs
                    flex items-center
                    gap-1
                    "
                    >
                      {getVendorName(product?.vendor)}
                    </p>
                  </div>
                </div>,
                <p
                  className="
                max-w-[100px] text-wrap break-words
                "
                  key={`${product._id}-price`}
                >
                  {formatCurrency(
                    calculateProductPriceRaw(
                      product.price,
                      [],
                      product.isMarkupEnabled ?? false,
                      product.markupValue ?? 0,
                    ),
                  )}
                </p>,
                product?.inStockQuantity?.toLocaleString() ?? 'N/A',
                <Status
                  key={`${product._id}-status`}
                  type={product.isActive ? 'success' : 'fail'}
                  text={product.isActive ? 'Active' : 'Inactive'}
                />,
                format(new Date(product.createdAt), 'dd/MM/yyyy h:mma'),
                <Dropdown
                  menuClassName="!overflow-x-visible !z-[900]"
                  className="!overflow-x-visible"
                  key={`${product._id}-controls`}
                  menuButton={
                    <Icon id="ellipses" height={18} width={18} className="" />
                  }
                  menuItems={[
                    {
                      name: 'Edit',
                      value: '',
                      onClick: () => {
                        navigate(`/products/view/${product._id}`)
                      },
                    },
                    {
                      name: 'Manage stock',
                      value: '',
                      hidden: product.isVariant,
                      onClick: () => {
                        setUpdateMode('quantity')
                        setSelected(product)
                        handleModalOpen('updateItemStockModal')
                      },
                    },
                    {
                      name: 'Manage price',
                      value: '',
                      onClick: () => {
                        setUpdateMode('price')
                        setSelected(product)
                        handleModalOpen('updateItemStockModal')
                      },
                    },
                  ]}
                />,
              ],
            }
          })}
          isEmpty={[undefined, 0].includes(productList.length)}
          emptyText="No products found"
          loading={loading}
          currentPage={currentPage}
          totalDataCount={totalDataCount}
          pageLimit={pageLimit}
          onPageChange={onPageChange}
        />
      </div>

      {selected && (
        <UpdateItemStockModal
          open={modalOpen && modal === 'updateItemStockModal'}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            handleModalClose()
            setSelected(undefined)
          }}
          onSuccessCallback={() => {
            handleModalClose()
            setSelected(undefined)
            refetch()
          }}
          product={selected}
          fulfilmentCentres={fulfilmentCentres}
          type={updateMode}
        />
      )}
    </>
  )
}

export default ProductsTable
